// src/pages/admin/AuditLogsTable.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    CircularProgress,
    Box,
    Collapse,
    Stack,
    Divider,
    Avatar,
    Card,
    CardContent,
    Link
} from '@mui/material';
import {
    Visibility as ViewIcon,
    KeyboardArrowDown as ExpandIcon,
    KeyboardArrowUp as CollapseIcon,
    Person as PersonIcon,
    LocalHospital as HospitalIcon,
    CalendarMonth as CalendarIcon,
    History as HistoryIcon,
    MedicalServices as ScanIcon,
    OpenInNew as OpenInNewIcon,
    EventNote as EventNoteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const AuditLogsTable = ({
    logs,
    loading,
    onViewDetails,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange,
    filters,
    isRTL
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [expandedRows, setExpandedRows] = useState({});

    const dateLocale = i18n.language === 'ar' ? ar : enUS;

    const toggleRowExpansion = (appointmentId, event) => {
        // Prevent navigation when clicking expand button
        event?.stopPropagation();
        setExpandedRows(prev => ({
            ...prev,
            [appointmentId]: !prev[appointmentId]
        }));
    };

    // Navigate to appointment history
    const handleNavigateToHistory = (appointmentId, event) => {
        event?.stopPropagation();
        if (appointmentId) {
            navigate(`/appointments/${appointmentId}/history`);
        }
    };

    const getActionChip = (action) => {
        const config = {
            CREATE: { color: 'success', label: t('audit.create', 'Create') },
            UPDATE: { color: 'primary', label: t('audit.update', 'Update') },
            DELETE: { color: 'error', label: t('audit.delete', 'Delete') },
            STATUS_CHANGE: { color: 'warning', label: t('audit.statusChange', 'Status Change') }
        };

        const { color, label } = config[action] || { color: 'default', label: action };

        return (
            <Chip
                label={label}
                color={color}
                size="small"
                variant="outlined"
            />
        );
    };

    const getStatusChip = (status) => {
        const config = {
            scheduled: { color: 'info', label: t('status.scheduled', 'Scheduled') },
            in_progress: { color: 'warning', label: t('status.inProgress', 'In Progress') },
            completed: { color: 'success', label: t('status.completed', 'Completed') },
            cancelled: { color: 'error', label: t('status.cancelled', 'Cancelled') }
        };

        const { color, label } = config[status] || { color: 'default', label: status };

        return (
            <Chip
                label={label}
                color={color}
                size="small"
            />
        );
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            return format(new Date(date), 'dd/MM/yyyy', { locale: dateLocale });
        } catch {
            return '-';
        }
    };

    const formatDateTime = (date) => {
        if (!date) return '-';
        try {
            return format(new Date(date), 'dd/MM/yyyy HH:mm:ss', { locale: dateLocale });
        } catch {
            return '-';
        }
    };

    const formatTime = (date) => {
        if (!date) return '-';
        try {
            return format(new Date(date), 'HH:mm', { locale: dateLocale });
        } catch {
            return '-';
        }
    };

    const getChangeSummary = (auditLog) => {
        const { action, changes } = auditLog;

        if (action === 'STATUS_CHANGE' && changes) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={changes.from} size="small" variant="outlined" />
                    <Typography variant="caption">→</Typography>
                    <Chip label={changes.to} size="small" color="primary" />
                </Box>
            );
        }

        if (action === 'CREATE') {
            return (
                <Typography variant="caption" color="success.main">
                    {t('audit.appointmentCreated', 'Appointment created')}
                </Typography>
            );
        }

        if (action === 'UPDATE') {
            return (
                <Typography variant="caption" color="info.main">
                    {t('audit.appointmentUpdated', 'Appointment updated')}
                </Typography>
            );
        }

        if (action === 'DELETE') {
            return (
                <Typography variant="caption" color="error.main">
                    {t('audit.appointmentDeleted', 'Appointment deleted')}
                </Typography>
            );
        }

        return (
            <Typography variant="caption" color="text.secondary">
                {t('audit.changesRecorded', 'Changes recorded')}
            </Typography>
        );
    };

    const getEmptyMessage = () => {
        if (filters?.action) {
            return t('audit.noActionResults', `No ${filters.action} logs found`);
        }
        if (filters?.startDate || filters?.endDate) {
            return t('audit.noDateRangeResults', 'No logs found in this date range');
        }
        return t('audit.noData', 'No audit logs found');
    };

    // Render clickable appointment link
    const renderAppointmentLink = (appointmentId, appointment, event) => {
        if (!appointmentId) {
            return (
                <Typography variant="body2" color="text.secondary">
                    -
                </Typography>
            );
        }

        return (
            <Box>
                <Tooltip title={t('audit.viewAppointmentHistory', 'View Appointment History')}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => handleNavigateToHistory(appointmentId, e)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            color: 'primary.main',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.dark'
                            }
                        }}
                    >
                        <EventNoteIcon fontSize="small" />
                        #{appointmentId?.slice(-6)}
                        <OpenInNewIcon fontSize="small" sx={{ opacity: 0.7, ml: 0.5 }} />
                    </Link>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" display="block">
                    {appointment?.scheduledAt
                        ? formatDate(appointment.scheduledAt)
                        : '-'}
                </Typography>
            </Box>
        );
    };

    // Render the expanded row content showing all audit logs for an appointment
    const renderExpandedContent = (groupedLog) => {
        const { appointment, auditLogs, appointmentId } = groupedLog;

        return (
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                {/* Appointment Details Card */}
                {appointment && (
                    <Card sx={{ mb: 2 }} variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t('audit.appointmentDetails', 'Appointment Details')}
                                </Typography>
                                <Tooltip title={t('audit.viewFullHistory', 'View Full History')}>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => handleNavigateToHistory(appointmentId, e)}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            '&:hover': {
                                                bgcolor: 'primary.main',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                {/* Patient Info */}
                                {appointment.patient && (
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                            <PersonIcon color="primary" fontSize="small" />
                                            <Typography variant="subtitle2" color="primary">
                                                {t('common.patient', 'Patient')}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" fontWeight="bold">
                                            {appointment.patient.name}
                                        </Typography>
                                        {appointment.patient.phone && (
                                            <Typography variant="caption" color="text.secondary">
                                                {appointment.patient.phone}
                                            </Typography>
                                        )}
                                        {appointment.patient.gender && (
                                            <Chip
                                                label={t(`gender.${appointment.patient.gender}`, appointment.patient.gender)}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </Box>
                                )}

                                {/* Branch Info */}
                                {appointment.branch && (
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                            <HospitalIcon color="secondary" fontSize="small" />
                                            <Typography variant="subtitle2" color="secondary">
                                                {t('common.branch', 'Branch')}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" fontWeight="bold">
                                            {appointment.branch.name}
                                        </Typography>
                                        {appointment.branch.address && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {appointment.branch.address}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Schedule Info */}
                                <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                        <CalendarIcon color="info" fontSize="small" />
                                        <Typography variant="subtitle2" color="info.main">
                                            {t('common.schedule', 'Schedule')}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2">
                                        {formatDate(appointment.scheduledAt)} - {formatTime(appointment.scheduledAt)}
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        {getStatusChip(appointment.status)}
                                    </Box>
                                </Box>

                                {/* Pricing Info */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                                        {t('common.pricing', 'Pricing')}
                                    </Typography>
                                    <Stack direction="row" spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('common.price', 'Price')}
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {appointment.price} {t('common.currency', 'EGP')}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('common.profit', 'Profit')}
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold" color="success.main">
                                                {appointment.profit} {t('common.currency', 'EGP')}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Chip
                                                label={appointment.isPaid ? t('common.paid', 'Paid') : t('common.unpaid', 'Unpaid')}
                                                color={appointment.isPaid ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>

                            {/* Scans */}
                            {appointment.scans && appointment.scans.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                        <ScanIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle2" color="primary">
                                            {t('common.scans', 'Scans')}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {appointment.scans.map((scanEntry, idx) => (
                                            <Chip
                                                key={scanEntry.id || idx}
                                                label={`${scanEntry.scan?.name || t('common.unknownScan', 'Unknown')} x${scanEntry.quantity}`}
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Referred By & Created By */}
                            <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
                                {appointment.referredBy && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('common.referredBy', 'Referred By')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {appointment.referredBy.name}
                                            {appointment.referredBy.specialization && (
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    ({appointment.referredBy.specialization.name || appointment.referredBy.specialization})
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {appointment.createdBy && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('common.createdBy', 'Created By')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {appointment.createdBy.username}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {/* Audit History Timeline */}
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                            <HistoryIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">
                                {t('audit.changeHistory', 'Change History')} ({auditLogs.length})
                            </Typography>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('common.action', 'Action')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('common.user', 'User')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('common.changes', 'Changes')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('common.timestamp', 'Timestamp')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('common.actions', 'Actions')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auditLogs.map((auditLog, index) => (
                                    <TableRow
                                        key={auditLog.id || index}
                                        sx={{
                                            bgcolor: index === 0 ? 'action.hover' : 'inherit',
                                            '&:last-child td': { borderBottom: 0 }
                                        }}
                                    >
                                        <TableCell>
                                            {getActionChip(auditLog.action)}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                                    {auditLog.user?.username?.charAt(0) || '?'}
                                                </Avatar>
                                                <Typography variant="body2">
                                                    {auditLog.user?.username || t('common.unknownUser', 'Unknown')}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {getChangeSummary(auditLog)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDateTime(auditLog.timestamp)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={t('common.viewDetails', 'View Details')}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => onViewDetails(auditLog, groupedLog)}
                                                >
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 50 }}>
                                {/* Expand/Collapse */}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.appointment', 'Appointment')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.patient', 'Patient')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.branch', 'Branch')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.status', 'Status')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center'
                                }}
                            >
                                {t('audit.totalChanges', 'Changes')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('audit.lastActivity', 'Last Activity')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center'
                                }}
                            >
                                {t('common.actions', 'Actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">
                                        {getEmptyMessage()}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((groupedLog) => {
                                const { appointmentId, appointment, auditLogs, totalChanges, latestChange } = groupedLog;
                                const isExpanded = expandedRows[appointmentId];

                                return (
                                    <React.Fragment key={appointmentId}>
                                        {/* Main Row */}
                                        <TableRow
                                            hover
                                            sx={{
                                                '& > *': { borderBottom: isExpanded ? 0 : undefined }
                                            }}
                                        >
                                            {/* Expand/Collapse Button */}
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => toggleRowExpansion(appointmentId, e)}
                                                >
                                                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                                                </IconButton>
                                            </TableCell>

                                            {/* Appointment ID & Date - Clickable */}
                                            <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                {renderAppointmentLink(appointmentId, appointment)}
                                            </TableCell>

                                            {/* Patient */}
                                            <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                {appointment?.patient ? (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {appointment.patient.name}
                                                        </Typography>
                                                        {appointment.patient.phone && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {appointment.patient.phone}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            {/* Branch */}
                                            <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                {appointment?.branch ? (
                                                    <Typography variant="body2">
                                                        {appointment.branch.name}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                {appointment?.status ? (
                                                    getStatusChip(appointment.status)
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            {/* Total Changes */}
                                            <TableCell align="center">
                                                <Chip
                                                    label={totalChanges}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            {/* Last Activity */}
                                            <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                <Box>
                                                    <Typography variant="body2">
                                                        {formatDateTime(latestChange)}
                                                    </Typography>
                                                    {auditLogs && auditLogs[0] && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {t('common.by', 'by')} {auditLogs[0].user?.username || t('common.unknown', 'Unknown')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <Tooltip title={t('audit.viewAppointmentHistory', 'View Appointment History')}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={(e) => handleNavigateToHistory(appointmentId, e)}
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: 'primary.light',
                                                                    color: 'primary.contrastText'
                                                                }
                                                            }}
                                                        >
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('common.viewDetails', 'View Details')}>
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (auditLogs && auditLogs[0]) {
                                                                    onViewDetails(auditLogs[0], groupedLog);
                                                                }
                                                            }}
                                                        >
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>

                                        {/* Expanded Content Row */}
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                sx={{ py: 0, px: 0 }}
                                            >
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    {renderExpandedContent(groupedLog)}
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total || 0}
                rowsPerPage={rowsPerPage || 10}
                page={page || 0}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                labelRowsPerPage={t('common.rowsPerPage', 'Rows per page:')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('common.of', 'of')} ${count !== -1 ? count : `${t('common.moreThan', 'more than')} ${to}`}`
                }
            />
        </Paper>
    );
};

export default AuditLogsTable;